/**
 * Spec 200 second-pass UX redesign R2 — Command palette (Cmd+K).
 *
 * Linear/Notion/Stripe pattern: a single chord opens a fuzzy-search
 * panel of every navigable + actionable thing in the app. Cuadrilla
 * web gets:
 *
 *   - Navigations: Inicio (WorkList), Nuevo trabajo, Ajustes.
 *   - Theme switcher: Claro / Oscuro / Auto.
 *   - Recent works: top 8 by `updatedAt` from IndexedDB. Click →
 *     `/works/:id`.
 *   - "Buscar 'X'" passthrough to WorkList (URL `?q=X`) when the
 *     query doesn't match a command.
 *
 * The whole component is ~200 lines of plain React + CSS — no new
 * dep. WAI-ARIA Combobox pattern: input has
 * `role="combobox" aria-controls="cmdk-list" aria-expanded="true"`,
 * the listbox uses `role="listbox"` + `aria-activedescendant` to
 * announce the highlighted option to screen readers.
 *
 * Trigger: `Cmd+K` (macOS) / `Ctrl+K` (Linux/Windows). Listener is
 * mounted at App level so the palette is reachable from anywhere.
 * `?` closes immediately; `Escape` always exits.
 *
 * The palette is intentionally NOT a state-machine library —
 * useState + a tiny `filtered` derivation is enough for this
 * surface and keeps the bundle footprint zero-add.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  BarChart3,
  Briefcase,
  Moon,
  Plus,
  Search,
  Settings as SettingsIcon,
  Sun,
  Monitor,
  Users as UsersIcon,
  type LucideIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { getWorks } from '../store/db';
import {
  THEME_STORAGE_KEY,
  type ThemePreference,
} from '../theme/preferences';
import type { Work } from '../types';

import './CommandPalette.css';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  icon: LucideIcon;
  action: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [recents, setRecents] = useState<Work[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset query + load recents whenever the palette opens.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setHighlight(0);
    inputRef.current?.focus();
    let cancelled = false;
    void getWorks().then((all) => {
      if (cancelled) return;
      const sorted = [...all].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 8);
      setRecents(sorted);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  function setTheme(next: ThemePreference) {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
      window.dispatchEvent(
        new StorageEvent('storage', { key: THEME_STORAGE_KEY, newValue: next }),
      );
    } catch {
      // Private mode — silent.
    }
  }

  // Static commands — navigation + theme + a few utilities. The
  // recents are appended dynamically below.
  const commands: CommandItem[] = useMemo(
    () => [
      {
        id: 'nav.home',
        label: 'Ir a Trabajos',
        hint: 'Inicio',
        icon: Briefcase,
        action: () => navigate('/'),
      },
      {
        id: 'nav.new',
        label: 'Crear trabajo nuevo',
        hint: 'N',
        icon: Plus,
        action: () => navigate('/works/new'),
      },
      {
        id: 'nav.stats',
        label: 'Ver estadísticas',
        hint: 'G E',
        icon: BarChart3,
        action: () => navigate('/stats'),
      },
      {
        id: 'nav.clients',
        label: 'Ver clientes',
        hint: 'G C',
        icon: UsersIcon,
        action: () => navigate('/clients'),
      },
      {
        id: 'nav.settings',
        label: 'Abrir Ajustes',
        hint: 'G S',
        icon: SettingsIcon,
        action: () => navigate('/settings'),
      },
      {
        id: 'theme.light',
        label: 'Tema claro',
        icon: Sun,
        action: () => setTheme('light'),
      },
      {
        id: 'theme.dark',
        label: 'Tema oscuro',
        icon: Moon,
        action: () => setTheme('dark'),
      },
      {
        id: 'theme.auto',
        label: 'Tema automático',
        hint: 'según el sistema',
        icon: Monitor,
        action: () => setTheme('auto'),
      },
    ],
    [navigate],
  );

  // Filter commands + recents by the query. Plain substring match
  // (case-insensitive, accent-stripped) — fuzzy-fuzz is overkill for
  // a list this size.
  const norm = (s: string) =>
    s
      .toLocaleLowerCase('es')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  const q = norm(query.trim());
  const filteredCommands = q
    ? commands.filter((c) => norm(c.label).includes(q) || (c.hint && norm(c.hint).includes(q)))
    : commands;
  const filteredRecents = q
    ? recents.filter((w) => norm(w.title).includes(q) || (w.clientName && norm(w.clientName).includes(q)))
    : recents;
  const hasSearchPassthrough = q.length > 0;

  // Flat list driven by `highlight` index — keyboard nav cycles the
  // current selection through commands → recents → search action.
  type Row =
    | { kind: 'command'; cmd: CommandItem }
    | { kind: 'recent'; work: Work }
    | { kind: 'search'; query: string };
  const rows: Row[] = [
    ...filteredCommands.map((cmd) => ({ kind: 'command' as const, cmd })),
    ...filteredRecents.map((work) => ({ kind: 'recent' as const, work })),
  ];
  if (hasSearchPassthrough) {
    rows.push({ kind: 'search', query: query.trim() });
  }

  function runRow(row: Row) {
    onClose();
    if (row.kind === 'command') row.cmd.action();
    else if (row.kind === 'recent') navigate(`/works/${row.work.id}`);
    else navigate(`/?q=${encodeURIComponent(row.query)}`);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(0, rows.length - 1)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[highlight];
      if (row) runRow(row);
    }
  }

  // Keep the highlighted row in view if it scrolls out.
  useEffect(() => {
    if (!open) return;
    const node = document.getElementById(`cmdk-row-${highlight}`);
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [highlight, open]);

  if (!open) return null;
  return (
    <div
      className="cmdk-backdrop"
      data-testid="command-palette"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="cmdk-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Paleta de comandos"
      >
        <div className="cmdk-input-row">
          <Search size={20} strokeWidth={2} aria-hidden="true" className="cmdk-icon" />
          <input
            ref={inputRef}
            type="text"
            className="cmdk-input"
            value={query}
            placeholder="Busca un trabajo o ejecuta una acción…"
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlight(0);
            }}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-list"
            aria-activedescendant={rows[highlight] ? `cmdk-row-${highlight}` : undefined}
            aria-autocomplete="list"
            data-testid="command-palette-input"
          />
        </div>

        <ul id="cmdk-list" role="listbox" className="cmdk-list">
          {rows.length === 0 && (
            <li className="cmdk-empty">No hay coincidencias.</li>
          )}
          {rows.map((row, idx) => {
            const active = idx === highlight;
            const id = `cmdk-row-${idx}`;
            if (row.kind === 'command') {
              const Icon = row.cmd.icon;
              return (
                <li
                  key={row.cmd.id}
                  id={id}
                  role="option"
                  aria-selected={active}
                  className={`cmdk-row ${active ? 'cmdk-row--active' : ''}`}
                  onMouseEnter={() => setHighlight(idx)}
                  onClick={() => runRow(row)}
                >
                  <Icon size={16} strokeWidth={2} aria-hidden="true" className="cmdk-row-icon" />
                  <span className="cmdk-row-label">{row.cmd.label}</span>
                  {row.cmd.hint && <span className="cmdk-row-hint">{row.cmd.hint}</span>}
                </li>
              );
            }
            if (row.kind === 'recent') {
              return (
                <li
                  key={`recent-${row.work.id}`}
                  id={id}
                  role="option"
                  aria-selected={active}
                  className={`cmdk-row ${active ? 'cmdk-row--active' : ''}`}
                  onMouseEnter={() => setHighlight(idx)}
                  onClick={() => runRow(row)}
                >
                  <Briefcase size={16} strokeWidth={2} aria-hidden="true" className="cmdk-row-icon" />
                  <span className="cmdk-row-label">{row.work.title}</span>
                  {row.work.clientName && (
                    <span className="cmdk-row-hint">{row.work.clientName}</span>
                  )}
                </li>
              );
            }
            return (
              <li
                key="search-passthrough"
                id={id}
                role="option"
                aria-selected={active}
                className={`cmdk-row cmdk-row--search ${active ? 'cmdk-row--active' : ''}`}
                onMouseEnter={() => setHighlight(idx)}
                onClick={() => runRow(row)}
              >
                <ArrowRight size={16} strokeWidth={2} aria-hidden="true" className="cmdk-row-icon" />
                <span className="cmdk-row-label">
                  Buscar «{row.query}» en trabajos
                </span>
              </li>
            );
          })}
        </ul>

        <footer className="cmdk-footer" aria-hidden="true">
          <span><kbd>↑</kbd><kbd>↓</kbd> navegar</span>
          <span><kbd>↵</kbd> abrir</span>
          <span><kbd>Esc</kbd> cerrar</span>
        </footer>
      </div>
    </div>
  );
}

/** Hook: register the global `Cmd+K` / `Ctrl+K` listener that
 *  toggles the palette. Mount once at App level. The hook returns
 *  `[open, setOpen]` so the caller renders `<CommandPalette open
 *  onClose={() => setOpen(false)} />`. */
export function useCommandPalette(): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      // Don't intercept when the user is typing in a field or the
      // palette is already open (its own onKeyDown owns the event).
      const editing = target instanceof HTMLInputElement
        || target instanceof HTMLTextAreaElement
        || (target instanceof HTMLElement && target.isContentEditable);
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      // `/` quick-open from anywhere except inside an input.
      if (!editing && !open && e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setOpen(true);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  return [open, setOpen];
}
